import React, { useRef } from "react";
import PropTypes from "prop-types";
import styles from "./QueueTabs.module.css";

/**
 * Filtro primário da inbox: as três filas. Combinável com o filtro de canal
 * (secundário). Rótulos em sentence case; contadores derivados client-side.
 */
const QUEUE_TABS = [
  { value: "INBOX", label: "Entrada" },
  { value: "WAITING", label: "Esperando" },
  { value: "DONE", label: "Finalizados" },
];

/**
 * Segmented control acessível (WAI-ARIA tabs) no topo do painel de conversas.
 * Roving tabindex + navegação por setas/Home/End; ativação automática ao mover.
 */
export default function QueueTabs({ active, counts, onChange }) {
  const tabRefs = useRef([]);

  const handleKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % QUEUE_TABS.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + QUEUE_TABS.length) % QUEUE_TABS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = QUEUE_TABS.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    onChange(QUEUE_TABS[nextIndex].value);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={styles.tablist} role="tablist" aria-label="Filtrar por fila">
      {QUEUE_TABS.map((tab, index) => {
        const isActive = active === tab.value;
        const count = counts?.[tab.value] ?? 0;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`queue-tab-${tab.value}`}
            aria-selected={isActive}
            aria-label={`${tab.label}: ${count} conversas`}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
            onClick={() => onChange(tab.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <span className={styles.tabLabel}>{tab.label}</span>
            <span className={styles.tabCount} aria-hidden="true">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

QueueTabs.propTypes = {
  active: PropTypes.oneOf(["INBOX", "WAITING", "DONE"]).isRequired,
  counts: PropTypes.shape({
    INBOX: PropTypes.number,
    WAITING: PropTypes.number,
    DONE: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
};
